import { useEffect, useRef } from 'react';
import { applyFaceEffect } from '../lib/faceFilterUtils';

/**
 * FaceFilter — magnifying-lens style face deformations on the live camera feed.
 *
 * Perf optimisations:
 *  - Internal canvas is capped at MAX_DIM (480 px on mobile, 720 px on desktop)
 *    so even old iPhones can keep up at ~30 FPS.
 *  - Render loop is throttled to FPS_CAP (30 FPS) using a timestamp gate inside
 *    requestAnimationFrame — avoids burning CPU at 120 FPS on ProMotion devices
 *    while still feeling smooth.
 */
const MAX_DIM_MOBILE = 480;
const MAX_DIM_DESKTOP = 720;
const FPS_CAP = 30;
const FRAME_INTERVAL_MS = 1000 / FPS_CAP;

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export default function FaceFilter({ videoRef, effect = 'none', colorFilter = 'none', active = false }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(0);
  const wavePhaseRef = useRef(0);

  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;
    const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: true });
    const maxDim = isMobile() ? MAX_DIM_MOBILE : MAX_DIM_DESKTOP;

    const render = (ts) => {
      // Throttle to FPS_CAP
      if (ts - lastFrameRef.current < FRAME_INTERVAL_MS) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      lastFrameRef.current = ts;

      if (video.readyState >= 2 && video.videoWidth) {
        // Downscale internal canvas. The CSS `object-fit: cover` upscales it
        // visually, so the user still sees a full-screen preview.
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const ratio = Math.min(1, maxDim / Math.max(vw, vh));
        const w = Math.round(vw * ratio);
        const h = Math.round(vh * ratio);

        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;

        ctx.save();
        // Mirror horizontally for selfie view
        ctx.scale(-1, 1);
        ctx.translate(-w, 0);
        ctx.filter = colorFilter || 'none';
        ctx.drawImage(video, 0, 0, w, h);
        ctx.filter = 'none';

        if (effect === 'wavy') wavePhaseRef.current += 0.08;

        applyFaceEffect({
          ctx, video, effect,
          width: w, height: h,
          phase: wavePhaseRef.current,
        });

        ctx.restore();
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, effect, colorFilter, videoRef]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{
        display: active ? 'block' : 'none',
        objectFit: 'cover',
      }}
    />
  );
}
